import { MONTHS } from '../../../../../constants/progressBanner'
import type {
    CurrentDate,
    DateValidationResult,
    EmploymentDatesInput,
    EmploymentDatesValidationResult,
    HandleKeyDownFunction,
    HandleNumericInputFunction,
} from './types'

export const handleKeyDown: HandleKeyDownFunction = e => {
    const allowedKeys = [
        'Backspace',
        'Delete',
        'ArrowLeft',
        'ArrowRight',
        'Tab',
        'Enter',
    ]

    if (
        !/^\d$/.test(e.key) &&
        !allowedKeys.includes(e.key) &&
        !e.ctrlKey &&
        !e.metaKey
    ) {
        e.preventDefault()
    }
}

export const handleNumericInput: HandleNumericInputFunction = (
    value,
    setValue
) => {
    const numericValue = value.replace(/[^0-9]/g, '')
    setValue(numericValue)
}

const getCurrentDate = (): CurrentDate => {
    const currentDate = new Date()
    return {
        year: currentDate.getFullYear(),
        month: currentDate.getMonth() + 1,
    }
}

const isNumericOnly = (value: string): boolean => {
    return /^\d+$/.test(value)
}

const isValidMonth = (month: string): boolean => {
    if (!isNumericOnly(month)) {
        return false
    }
    const parsedMonth = parseInt(month)
    return parsedMonth >= MONTHS.MIN && parsedMonth <= MONTHS.MAX
}

const isValidYearRange = (year: string): boolean => {
    if (!isNumericOnly(year)) {
        return false
    }

    const currentYear = new Date().getFullYear()
    const minYear = currentYear - 100
    const parsedYear = parseInt(year)

    return parsedYear >= minYear && parsedYear <= currentYear
}

const isValidYear = (year: string): boolean => {
    return isNumericOnly(year) && isValidYearRange(year)
}

const validateStartDate = (
    startYear: string,
    startMonth: string
): DateValidationResult => {
    if (!startYear || !startMonth) {
        return {
            isValid: false,
            error: '勤務開始年月を入力してください',
        }
    }

    if (!isNumericOnly(startYear)) {
        return {
            isValid: false,
            error: '勤務開始年は数字のみ入力してください',
        }
    }

    if (!isValidYearRange(startYear)) {
        return {
            isValid: false,
            error: '有効な値を入力してください',
        }
    }

    if (!isValidMonth(startMonth)) {
        if (!isNumericOnly(startMonth)) {
            return {
                isValid: false,
                error: '勤務開始月は数字のみ入力してください',
            }
        }
        return {
            isValid: false,
            error: `有効な月を入力してください（${MONTHS.MIN}-${MONTHS.MAX}）`,
        }
    }

    const { year: currentYear, month: currentMonth } = getCurrentDate()
    const parsedStartYear = parseInt(startYear)
    const parsedStartMonth = parseInt(startMonth)

    if (
        parsedStartYear > currentYear ||
        (parsedStartYear === currentYear && parsedStartMonth > currentMonth)
    ) {
        return {
            isValid: false,
            error: '勤務開始日は未来の日付を指定できません',
        }
    }

    return {
        isValid: true,
        error: '',
    }
}

const validateEndDate = (
    startYear: string,
    startMonth: string,
    endYear: string,
    endMonth: string
): DateValidationResult => {
    if (!endYear || !endMonth) {
        return {
            isValid: false,
            error: '勤務終了年月を入力してください',
        }
    }

    if (!isNumericOnly(endYear)) {
        return {
            isValid: false,
            error: '勤務終了年は数字のみ入力してください',
        }
    }

    if (!isValidYearRange(endYear)) {
        return {
            isValid: false,
            error: '有効な値を入力してください',
        }
    }

    if (!isValidMonth(endMonth)) {
        if (!isNumericOnly(endMonth)) {
            return {
                isValid: false,
                error: '勤務終了月は数字のみ入力してください',
            }
        }
        return {
            isValid: false,
            error: `有効な月を入力してください（${MONTHS.MIN}-${MONTHS.MAX}）`,
        }
    }

    const parsedStartYear = parseInt(startYear)
    const parsedStartMonth = parseInt(startMonth)
    const parsedEndYear = parseInt(endYear)
    const parsedEndMonth = parseInt(endMonth)

    if (
        parsedStartYear > parsedEndYear ||
        (parsedStartYear === parsedEndYear &&
            parsedStartMonth > parsedEndMonth)
    ) {
        return {
            isValid: false,
            error: '勤務終了日は勤務開始日より後の日付を指定してください',
        }
    }

    return {
        isValid: true,
        error: '',
    }
}

export const validateEmploymentDates = ({
    workStartYear,
    workStartMonth,
    workEndYear,
    workEndMonth,
    isTenure,
}: EmploymentDatesInput): EmploymentDatesValidationResult => {
    const startDateValidation = validateStartDate(workStartYear, workStartMonth)

    if (isTenure) {
        return {
            isValid: startDateValidation.isValid,
            startDateError: startDateValidation.error,
            endDateError: '',
        }
    }

    const endDateValidation = validateEndDate(
        workStartYear,
        workStartMonth,
        workEndYear as string,
        workEndMonth as string
    )

    return {
        isValid: startDateValidation.isValid && endDateValidation.isValid,
        startDateError: startDateValidation.error,
        endDateError: endDateValidation.error,
    }
}
